export const PROCESSING_STEP_EVENT = 'processing_step';

const STEP_STARTED = 'started';
const STEP_DONE = 'done';

/**
 * What the agent is doing right now, from the `processing_step` frames it streams while a turn runs.
 *
 * ⚠ AN OPEN STEP IS TRACKED BY `step_id`, NEVER BY ORDER. The model can call tools together, so steps overlap:
 * `started a, started b, done a` leaves `b` running. The newest still-open step is the one shown.
 *
 * ⚠ NO COUNTER, NO PERCENTAGE. `step_total` is "steps announced so far", not a forecast, and it grows during the
 * turn, so only the label is ever shown.
 */
export const agentChatStepsHelpers = {
  /** Whether a frame carries an `event` key: only progress frames do, a real reply never does. */
  isEventFrame: (frame) => Boolean(frame) && typeof frame === 'object' && 'event' in frame,

  /** Whether the frame is a progress frame this client knows how to draw. Any other `event` is ignored. */
  isProcessingStepFrame: (frame) => agentChatStepsHelpers.isEventFrame(frame) && frame.event === PROCESSING_STEP_EVENT,

  /**
   * The open steps after `frame`, oldest first. A `started` frame opens a step (or renames one already open), a
   * `done` frame closes it. A frame without a usable `step_id`, or a `done` for a step never opened, changes nothing.
   */
  applyStep: (openSteps, frame) => {
    const {step_id: stepId, label, status} = frame || {};
    if (!stepId) return openSteps;
    if (status === STEP_DONE) return openSteps.filter((step) => step.stepId !== stepId);
    if (status !== STEP_STARTED || typeof label !== 'string' || !label) return openSteps;
    if (openSteps.some((step) => step.stepId === stepId)) {
      return openSteps.map((step) => (step.stepId === stepId ? {stepId, label} : step));
    }
    return [...openSteps, {stepId, label}];
  },

  /**
   * The label of the newest open step. With none open, `fallback` (the label already on screen): the agent is still
   * working between two steps, and the line dropping to "Wait a sec..." for a second and back reads as a glitch.
   */
  getCurrentLabel: (openSteps, fallback = null) => (openSteps.length > 0 ? openSteps[openSteps.length - 1].label : fallback),

  /**
   * `query` with `events=processing_step` added, merged into a caller's own comma-separated `events` without
   * duplicates. The backend only sends progress frames to a connection that asks for them this way.
   */
  withProcessingStepEvents: (query = {}) => {
    const requested = String(query.events || '').split(',').map((name) => name.trim()).filter(Boolean);
    const events = requested.includes(PROCESSING_STEP_EVENT) ? requested : [...requested, PROCESSING_STEP_EVENT];
    return {...query, events: events.join(',')};
  },
};
